import React, { FC, useContext } from "react";
import { useTranslation } from "react-i18next";

import { IHttpRequestConfig } from "@/browser";
import { ISelectProps, Select, SelectOption } from "@/components/Select";
import { Text } from "@/components/Text";
import { ConsultantProfileContext } from "@/components/User";
import { fetchConsultantProfileManagerList, IConsultantProfile } from "@/data/consultant-profiles";
import { formatFullName } from "@/data/persons";
import { IFetchUsersListParams } from "@/data/users";
import { codelistFactory, Flex, ReactSelectComponents } from "@/eworkitComponents";
import { withFormikField } from "@/formikComponents";

import { Avatar } from "../Person";

export interface ICodelistUsersSelectProps extends ISelectProps<IConsultantProfile> {}

const CodelistManagersComponent: FC<ICodelistUsersSelectProps> = ({ ...props }) => {
  const ConsultantProfile = useContext(ConsultantProfileContext);

  const { t } = useTranslation("session");

  const valueRenderer = (option: IConsultantProfile) => {
    return (
      <>
        <Text text={formatFullName(option)} hjSuppress />
        {option.id === ConsultantProfile.currentProfileId && (
          <Text text={` (${t("COMPONENTS.SELECT_MANAGERS.YOU")})`} color="grey2" hjSuppress />
        )}
      </>
    );
  };

  const optionRenderer = (option: IConsultantProfile) => {
    return (
      <Flex direction="row" innerMargin="sm">
        <Avatar
          avatar={option.avatar}
          firstName={option.firstName}
          lastName={option.lastName}
          size="xl"
          theme="secondary"
          hjSuppress
        />
        <Flex direction="column">
          <Flex direction="row">
            <Text size="lg" weight="bold" text={formatFullName(option)} hjSuppress />
            {option.id === ConsultantProfile.currentProfileId && (
              <Text
                size="lg"
                whiteSpace="pre"
                text={` (${t("COMPONENTS.SELECT_MANAGERS.YOU")})`}
                color="grey2"
                hjSuppress
              />
            )}
          </Flex>

          <Text hjSuppress>{option.email}</Text>
        </Flex>
      </Flex>
    );
  };

  const selectProps: ISelectProps<IConsultantProfile> = {
    ...props,
    valueKey: "id",
    labelKey: (option: any) => formatFullName(option),
    components: {
      MultiValueLabel: ({ children, ...p }) => {
        return (
          <ReactSelectComponents.MultiValueLabel {...p}>{valueRenderer(p.data)}</ReactSelectComponents.MultiValueLabel>
        );
      },
      SingleValue: ({ children, ...p }) => {
        return <ReactSelectComponents.SingleValue {...p}>{valueRenderer(p.data)}</ReactSelectComponents.SingleValue>;
      },
      Option: ({ children, ...p }) => {
        return <SelectOption {...p}>{optionRenderer(p.data)}</SelectOption>;
      },
    },
  };

  return <Select {...(selectProps as any)} />;
};

const fetchProxy = async (query: IFetchUsersListParams, options: IHttpRequestConfig) => {
  const results = await fetchConsultantProfileManagerList({ size: 9999, ...query }, options);
  return results.content;
};

export const CodelistManagersSelect = codelistFactory<ISelectProps, IConsultantProfile, IFetchUsersListParams>(
  fetchProxy,
  CodelistManagersComponent,
);

export const FormikCodelistManagersSelect = withFormikField(CodelistManagersSelect);
